import { Effect } from 'effect';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { sessionPromptEffect } from '../client';
import { createQueryCache, querySchemaKey } from './query-cache';
import { useRequestOwner } from './use-request-owner';
import type { GenerateResult, OkraSession, StructuredSchema } from '../types';
import { okraHookFailure, runOkraHookEffect, type OkraHookError } from './hook-io';
import { useOkra } from './provider';

const DEFAULT_CACHE_TIME = 5 * 60 * 1000;
const cache = createQueryCache<GenerateResult<unknown>>();

export interface UseDocumentQueryOptions<T = undefined> {
  /** Document ID (e.g. "doc-xxx" or "ocr-xxx") */
  documentId: string | null;
  /** The query/prompt to run against the document */
  query: string;
  /** JSON schema or Zod schema for structured output */
  schema?: StructuredSchema<T>;
  /** Skip the query (e.g. while waiting for doc to be ready) */
  skip?: boolean;
  /** Model override */
  model?: string;
  /** Timeout in ms */
  timeoutMs?: number;
  /** How long cached results stay valid in ms (default: 300000 / 5 min) */
  cacheTime?: number;
}

export interface UseDocumentQueryReturn<T = undefined> {
  data: T | null;
  result: GenerateResult<T> | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface DocumentQueryEffectOptions<T = undefined> {
  schema?: StructuredSchema<T>;
  model?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
}

/**
 * Native one-shot document query — the IO seam behind {@link useDocumentQuery}.
 *
 * The prompt text and the model response never reach the failure payload; the
 * tagged error carries only the operation label, and the Promise adapter
 * restores the original rejection for the hook's abort/`instanceof` checks.
 */
export function runDocumentQueryEffect<T = undefined>(
  session: OkraSession,
  query: string,
  options: DocumentQueryEffectOptions<T> = {},
): Effect.Effect<GenerateResult<T>, OkraHookError> {
  return sessionPromptEffect<T>(session, query, options).pipe(
    Effect.mapError((error) =>
      okraHookFailure('documentQuery', 'Document query failed', error.cause),
    ),
  );
}

/**
 * Run a one-shot query against a document, optionally with structured output.
 * Results are cached in-memory for `cacheTime` ms (default 5 min).
 *
 * ```tsx
 * const { data } = useDocumentQuery({
 *   documentId: "doc-xxx",
 *   query: "Generate 4 chat suggestions",
 *   schema: z.object({ suggestions: z.array(z.object({ id: z.string(), text: z.string() })) }),
 *   skip: !isReady,
 * })
 * ```
 */
export function useDocumentQuery<T = undefined>(
  options: UseDocumentQueryOptions<T>,
): UseDocumentQueryReturn<T> {
  const { client } = useOkra();
  const {
    documentId,
    query,
    schema,
    skip = false,
    model,
    timeoutMs,
    cacheTime = DEFAULT_CACHE_TIME,
  } = options;

  const schemaKey = useMemo(() => querySchemaKey(schema), [schema]);
  const key = JSON.stringify([documentId, query, schemaKey, model ?? null]);
  // Equivalent JSON schemas retain the same selection; parser identity is part of schemaKey.
  const selection = useMemo(
    () => ({ client, documentId, query, schema, model, key }),
    [client, key],
  );
  const lifetime = useMemo(() => ({}), [selection, skip, timeoutMs, cacheTime]);
  const owner = useRequestOwner(lifetime, true);
  const [state, setState] = useState<{
    selection: object;
    result: GenerateResult<T> | null;
    isLoading: boolean;
    error: Error | null;
  }>({ selection, result: null, isLoading: false, error: null });

  const doFetch = useCallback(
    async (bypassCache = false) => {
      if (!selection.documentId || !selection.query.trim() || skip) return;
      // Invalidate the previous operation before checking the cache, including explicit refetches.
      const request = owner.begin();
      if (!request) return;
      const isCurrent = () => owner.owns(request);
      setState((current) => ({
        selection,
        result: current.selection === selection ? current.result : null,
        isLoading: true,
        error: null,
      }));
      try {
        const result = await runOkraHookEffect(
          Effect.gen(function* () {
            if (!bypassCache) {
              const hit = yield* cache.get(selection.client, selection.key, cacheTime);
              if (hit) return hit as GenerateResult<T>;
            }
            const session = yield* Effect.try({
              try: () => selection.client.sessions.from(selection.documentId!),
              catch: (cause) => okraHookFailure('documentQuery', 'Document query failed', cause),
            });
            const result = yield* runDocumentQueryEffect<T>(session, selection.query, {
              schema: selection.schema,
              model: selection.model,
              timeoutMs,
            });
            yield* cache.put(selection.client, selection.key, result, isCurrent);
            return result;
          }),
          request.signal,
        );
        if (isCurrent()) setState({ selection, result, isLoading: false, error: null });
      } catch (cause) {
        if (isCurrent())
          setState((current) => ({
            ...current,
            isLoading: false,
            error: cause instanceof Error ? cause : new Error(String(cause)),
          }));
      }
    },
    [selection, skip, timeoutMs, cacheTime, owner],
  );

  useEffect(() => {
    setState((current) => ({
      selection,
      result: current.selection === selection ? current.result : null,
      isLoading: false,
      error: null,
    }));
    void doFetch();
  }, [selection, doFetch]);

  const current =
    state.selection === selection ? state : { result: null, isLoading: false, error: null };
  return {
    data: current.result?.data ?? null,
    result: current.result,
    isLoading: !skip && Boolean(documentId && query.trim()) && current.isLoading,
    error: current.error,
    refetch: () => {
      void doFetch(true);
    },
  };
}
